/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  static eventName = 'giesto:recently-viewed-updated';

  /** @static @constant {string} The key used to store viewed product IDs in local storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {string} The key used to store viewed product handles in local storage */
  static #DETAILS_STORAGE_KEY = 'viewedProductDetails';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   * @param {string} [productHandle] - The product handle used to hydrate product cards.
   */
  static addProduct(productId, productHandle = '') {
    const id = String(productId ?? '');
    const handle = String(productHandle ?? '').trim();
    if (!id) return;

    try {
      let viewedProducts = this.getProducts();
      viewedProducts = viewedProducts.filter((viewedProductId) => viewedProductId !== id);
      viewedProducts.unshift(id);
      viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));

      let viewedProductDetails = this.getProductDetails().filter((product) => product.id !== id);
      if (handle) viewedProductDetails.unshift({ id, handle });
      viewedProductDetails = viewedProductDetails.slice(0, this.#MAX_PRODUCTS);
      localStorage.setItem(this.#DETAILS_STORAGE_KEY, JSON.stringify(viewedProductDetails));
    } catch (_error) {
      return;
    }

    document.dispatchEvent(
      new CustomEvent(this.eventName, {
        detail: { productId: id, productHandle: handle },
      })
    );
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
    localStorage.removeItem(this.#DETAILS_STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed product IDs from local storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return this.#read(this.#STORAGE_KEY)
      .map((productId) => String(productId ?? ''))
      .filter(Boolean);
  }

  /**
   * Retrieves recently viewed products with handles, in recency order.
   * Existing ID-only history remains available to predictive search.
   * @returns {Array<{id: string, handle: string}>}
   */
  static getProductDetails() {
    const detailsById = new Map();

    this.#read(this.#DETAILS_STORAGE_KEY).forEach((value) => {
      if (!value || typeof value !== 'object') return;

      const id = String(value.id ?? '');
      const handle = String(value.handle ?? '').trim();
      if (id && handle && !detailsById.has(id)) detailsById.set(id, { id, handle });
    });

    return this.getProducts().flatMap((id) => {
      const product = detailsById.get(id);
      return product ? [product] : [];
    });
  }

  /** @param {string} key */
  static #read(key) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (_error) {
      return [];
    }
  }
}
